import { gql } from '@apollo/client';
import { weekKeyOf, weekRange } from '@infras/shared-types';

/**
 * Data model of the weekly board (/tasks/board). The server owns membership: TODO and
 * IN PROGRESS are every open task with that `workflow::` label, DONE is the tasks
 * moved to done in the chosen VN week (or the last 30 days for "all"). Every column is in board
 * order: the place someone dragged a card to first, cards never placed at the bottom. The client
 * only labels weeks (week.ts) and keeps an optimistic overlay while a move is in flight.
 */

/** A column as the API names it; `none` is the "No workflow label" source column. */
export type ColumnKey = 'none' | 'todo' | 'in_progress' | 'done';

export const COLUMNS: { key: ColumnKey; title: string }[] = [
  { key: 'none', title: 'No workflow label' },
  { key: 'todo', title: 'TODO' },
  { key: 'in_progress', title: 'IN PROGRESS' },
  { key: 'done', title: 'DONE' },
];
export const columnTitle = (k: ColumnKey) => COLUMNS.find((c) => c.key === k)?.title ?? k;

export interface BoardLabel {
  id: number;
  name: string;
  scope: string | null;
  color: string;
  system: boolean;
}

export interface BoardCardData {
  id: number;
  title: string;
  template: string;
  state: string;
  dueAt: string | null;
  /** Everyone assigned, ordered by email. */
  assignees: { email: string }[];
  requesterEmail: string;
  updatedAt: string;
  commentCount: number;
  workflow: string | null;
  labels: BoardLabel[];
  access: { status: string } | null;
  /** Board cards only: VN week the workflow label was applied in, and whether that is before the board's week. */
  since?: string;
  carried?: boolean;
}

const CARD_FIELDS = `
  id
  title
  template
  state
  dueAt
  assignees { email }
  requesterEmail
  updatedAt
  commentCount
  workflow
  labels { id name scope color system }
  access { status }
`;

export const BOARD_QUERY = gql`
  query TaskBoard($week: String, $assignee: String, $label: String, $q: String) {
    taskBoard(week: $week, assignee: $assignee, label: $label, q: $q) {
      week
      currentWeek
      todo { ${CARD_FIELDS} since carried }
      inProgress { ${CARD_FIELDS} since carried }
      done { ${CARD_FIELDS} since carried }
    }
  }
`;

/** The source column: `tasks(filter: {state: [open], workflow: [none], sort: board, …})` — open
 *  tasks with no `workflow::*` label in board order (manual place first, the rest at the bottom
 *  by last update), paged with offset ("Load more"). */
export const UNPLANNED_QUERY = gql`
  query TaskBoardUnplanned($filter: TaskFilterInput) {
    tasks(filter: $filter) {
      total
      items { ${CARD_FIELDS} }
    }
  }
`;
export const UNPLANNED_PAGE = 50;

export const LABELS_QUERY = gql`
  query TaskBoardLabels {
    labels {
      id
      name
      scope
      color
      system
    }
  }
`;

/** `afterId` / `beforeId` are the VISIBLE neighbours in the target column (null at the top /
 *  bottom; both null = the bottom of another column). `week` is the board's week view, so a
 *  DONE reorder places the card among the cards the viewer sees. */
export const MOVE_MUTATION = gql`
  mutation MoveTaskOnBoard($id: Int!, $to: String, $expectedFrom: String, $afterId: Int, $beforeId: Int, $week: String) {
    moveTaskOnBoard(id: $id, to: $to, expectedFrom: $expectedFrom, afterId: $afterId, beforeId: $beforeId, week: $week) {
      id
      state
      workflow
    }
  }
`;

export interface BoardData {
  taskBoard: {
    week: string;
    currentWeek: string;
    todo: BoardCardData[];
    inProgress: BoardCardData[];
    done: BoardCardData[];
  };
}

/** The API's `to` / `expectedFrom` for a column (`none` = off the board = null). */
export const apiColumn = (k: ColumnKey): string | null => (k === 'none' ? null : k);

/** The column a task's `workflow` field ('in-progress' etc.) puts it in. */
export function columnOfWorkflow(w: string | null | undefined): ColumnKey {
  if (w === 'todo') return 'todo';
  if (w === 'in-progress' || w === 'in_progress') return 'in_progress';
  if (w === 'done') return 'done';
  return 'none';
}

/** Week key `n` weeks away from `key` (Monday-start, VN time). */
export function shiftWeek(key: string, n: number): string {
  const { start } = weekRange(key);
  return weekKeyOf(start.getTime() + n * 7 * 24 * 60 * 60 * 1000 + 60 * 60 * 1000);
}

const DAY_FMT = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  timeZone: 'Asia/Ho_Chi_Minh',
});

/** "12 Oct – 18 Oct" for a week key (Monday to Sunday, VN time). Empty string on a bad key. */
export function weekSpan(key: string): string {
  try {
    const { start, end } = weekRange(key);
    return `${DAY_FMT.format(start)} – ${DAY_FMT.format(new Date(end.getTime() - 1))}`;
  } catch {
    return '';
  }
}

/** "2026-W41" → "W41". */
export const shortWeek = (key: string) => key.replace(/^\d{4}-/, '');

/** A conflict refusal from moveTaskOnBoard ("conflict: task 12 is in todo now …"). */
export const isConflict = (msg: string) => /\bconflict:/i.test(msg);

/** The GraphQL error codes on an Apollo 4 rejection (CombinedGraphQLErrors `.errors`, or a
 *  ServerError's parsed `result.errors`). Guarded: these come off the wire. */
function errorCodes(e: unknown): string[] {
  const x = e as { errors?: unknown; result?: { errors?: unknown } } | null;
  const list = [
    ...(Array.isArray(x?.errors) ? x.errors : []),
    ...(Array.isArray(x?.result?.errors) ? x.result.errors : []),
  ] as { extensions?: { code?: unknown } }[];
  return list.map((g) => g?.extensions?.code).filter((c): c is string => typeof c === 'string');
}

/** A board move conflict: the api's `extensions.code === 'CONFLICT'` (a 409), or — for an
 *  older api that still reports it as INTERNAL_SERVER_ERROR — the `conflict:` message prefix. */
export const isConflictError = (e: unknown, msg: string) => errorCodes(e).includes('CONFLICT') || isConflict(msg);
